import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, FormControl, Grid, Typography } from '@material-ui/core';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { ArcherContainer, ArcherElement } from 'react-archer';
import { useSelector } from 'react-redux';
import { StyledButton } from '../../../../../App/Shifamed/Components/forms/presenters/StyledListButton';
import { translate } from '../../../../../common/intl';
import { documentTypeSelectors } from '../../../../../state/ducks/documentRevisions/documentType';
import { DocumentType } from '../../../../../state/ducks/documentRevisions/documentType/types';
import { DocumentRevision } from '../../../../../state/ducks/documentRevisions/types';
import Loader from '../../../../components/common/kendo/Loader';
import { Autocomplete, Checkbox, FormControlLabel } from '../../../../components/forms/fields-next';
import Text from '../../../../components/Text';
import { FBSchemaProps } from '../../../../form.builder';
import { DialogHandler } from '../../../../hooks/useDialog';
import { Colors } from '../../../../layout/theme-next';
import { FormFieldSchema, ListType } from '../../../bulkMigration/interface';
import { useBulkMigration } from '../../../bulkMigration/useBulkMigration';
import { getFromSchemaItemBgColor, isDisabledSchemaItem } from '../../../bulkMigration/utils';
import { EditableApprovalConfiguration } from '../../panels/ApprovalConfigurations/types';
import { ApprovalConfigurationStage, SourceMapping, toSortedOptions } from '../../types';
import useStyles from './stageFieldMappingDialog.styles';

interface StageFieldMappingDialogProps {
  mappingDialog: DialogHandler
  configData: EditableApprovalConfiguration
  handleMappingUpdate: (
    approvalStages: ApprovalConfigurationStage[], selectedDocumentTypeId: string | undefined, formRevId: string | undefined) => void
}

const StageFieldMappingDialog: React.FC<StageFieldMappingDialogProps> = ({
  mappingDialog,
  configData,
  handleMappingUpdate,
}) => {
  const classes = useStyles();

  const {
    isLoading,
    toFormRevsList: formRevsList,
    fetchFormRevsForDocumentType,
  } = useBulkMigration();

  let documentTypesAll: DocumentType[] = useSelector(documentTypeSelectors.getDocumentTypeList);
  documentTypesAll = documentTypesAll.filter(documentType => documentType.active);
  const documentTypeOptions = toSortedOptions(documentTypesAll,
    (documentType) => documentType.id,
    (documentType) => documentType.documentTypeName,
    (documentType) => ({ documentTypeAcronym: documentType.documentTypeAcronym }),
  );

  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<string | undefined>(configData.documentTypeIds_edit?.[0]?.id);
  const [formRevId, setFormRevId] = useState<string | undefined>();
  const [formDocumentId, setFormDocumentId] = useState<string | undefined>(configData.formDocumentId);

  const [formRevSchema, setFormRevSchema] = useState<FormFieldSchema[]>([]);
  const [approvalStages, setApprovalStages] = useState<ApprovalConfigurationStage[]>(configData.approvalStages ?? []);

  const [isShowRelationVisible, setIsShowRelationVisible] = useState<boolean>(false);
  const [selectedSchemaItem, setSelectedSchemaItem] = useState<FormFieldSchema | undefined>();

  const [unmappedFieldsCount, setUnMappedFieldsCount] = useState<number>(0);

  useEffect(() => {
    if (selectedDocumentTypeId) {
      fetchFormRevsForDocumentType(selectedDocumentTypeId, ListType.TO);
    }
  }, [selectedDocumentTypeId]);

  useEffect(() => {
    if (formDocumentId) {
      const selectedForm = formRevsList.find(formRev => formRev.document?.id === formDocumentId);
      if (selectedForm) {
        setFormRevId(selectedForm.id);
      }
    }
  }, [formRevsList, formDocumentId]);

  useEffect(() => {
    if (formRevId && formRevsList.length) {
      const formRev = formRevsList.find(formRev => formRev.id === formRevId);
      const schema = formRev?.formTemplate?.schema ?? [];
      if (schema.length) {
        const allMappedFields: string[] = approvalStages.flatMap(stage => stage.editableFields ?? []);
        const schemaToMap = schema
          .filter(schemaItem => schemaItem.name && !schemaItem.name?.includes('fb-approvals'))
          .map(schemaItem => ({ ...schemaItem, mappedToCount: allMappedFields.includes(schemaItem.name!) ? 1 : 0 }));

        const schemaWithoutSections = schemaToMap
          .filter(schemaItem => schemaItem.type !== 'section' && schemaItem.type !== 'subsection');
        setFormRevSchema(schemaToMap);
        const schemaLength = schemaWithoutSections.length;

        const fieldsMapped = new Set<string>();
        if (approvalStages.length) {
          const mappedApprovalStages = approvalStages.map(approvalStage => {
            const mappedFrom: SourceMapping[] = [];
            for (const editableField of (approvalStage.editableFields ?? [])) {
              const matchingSchemaItem = schemaToMap.find(schemaItem => schemaItem.name === editableField);
              if (matchingSchemaItem) {
                mappedFrom.push({
                  name: matchingSchemaItem.name!,
                  label: matchingSchemaItem.label,
                  type: matchingSchemaItem.type,
                });
                fieldsMapped.add(matchingSchemaItem.name!);
              }
            }

            return { ...approvalStage, mappedFrom };
          });
          const fieldsUnmapped = schemaLength - fieldsMapped.size;
          setUnMappedFieldsCount(fieldsUnmapped);
          setApprovalStages(mappedApprovalStages);
        }
      }
    } else {
      setFormRevSchema([]);
    }
  }, [formRevId, formRevsList]);

  const checkIfRequired = (schemaItem: FormFieldSchema) => {
    if (!schemaItem.rules) {
      return <></>;
    }

    if (schemaItem.rules?.startsWith('required')) {
      return <span className={classes.isRequired}>*</span>;
    }
  };

  const handleFromSchemaItemClick = (schemaItem: FormFieldSchema) => {
    if (!isDisabledSchemaItem(schemaItem)) {
      setSelectedSchemaItem(schemaItem);
    }

    if (selectedSchemaItem?.name === schemaItem.name) {
      setSelectedSchemaItem(undefined);
    }
  };

  const setFromRevSchemaItemMapped = (selectedSchemaItem: FormFieldSchema) => {
    setFormRevSchema(
      formRevSchema.map((fromRevSchemaItem) => {
        if (fromRevSchemaItem.name === selectedSchemaItem.name) {
          fromRevSchemaItem.mappedToCount = (fromRevSchemaItem.mappedToCount ?? 0) + 1;
        }
        return fromRevSchemaItem;
      }),
    );
  };

  const handleApprovalStageClick = (approvalStage: ApprovalConfigurationStage) => {
    if (!selectedSchemaItem) {
      return;
    }

    mapSelectedSchemaItemToApprovalStage(approvalStage, selectedSchemaItem);
  };

  function mapSelectedSchemaItemToApprovalStage (
    selectedApprovalStage: ApprovalConfigurationStage,
    selectedSchemaItem: FBSchemaProps,
  ) {
    setApprovalStages(
      approvalStages.map((approvalStage) => {
        if (approvalStage.stageNumber === selectedApprovalStage.stageNumber) {
          if (!approvalStage.mappedFrom) {
            approvalStage.mappedFrom = [];
          }
          approvalStage.mappedFrom.push({
            name: selectedSchemaItem.name!,
            label: selectedSchemaItem.label!,
            type: selectedSchemaItem.type,
          });
        }
        return approvalStage;
      }),
    );

    // Set isMapped on fromRevSchema item to true
    setFromRevSchemaItemMapped(selectedSchemaItem);
    // setSelectedSchemaItem(undefined);
  }

  const handleMappedFromDelete = (
    selectedApprovalStage: ApprovalConfigurationStage, mappedFromEntryToRemove: SourceMapping,
  ) => {
    setApprovalStages(
      approvalStages.map((approvalStage) => {
        if (approvalStage.stageNumber === selectedApprovalStage.stageNumber) {
          approvalStage.mappedFrom = approvalStage.mappedFrom?.filter(
            (mappedFromItem) => mappedFromItem.name !== mappedFromEntryToRemove.name,
          );
        }
        return approvalStage;
      }),
    );

    setFormRevSchema(
      formRevSchema.map((fromRevSchemaItem) => {
        if (fromRevSchemaItem.name === mappedFromEntryToRemove.name) {
          if (fromRevSchemaItem.mappedToCount) {
            fromRevSchemaItem.mappedToCount -= 1;
          }
        }
        return fromRevSchemaItem;
      }),
    );
  };

  const onClearMappings = () => {
    setApprovalStages(
      approvalStages.map((approvalStage) => {
        approvalStage.mappedFrom = [];
        return approvalStage;
      }),
    );

    setFormRevSchema(
      formRevSchema.map((fromRevSchemaItem) => {
        fromRevSchemaItem.mappedToCount = 0;
        return fromRevSchemaItem;
      }),
    );
  };

  const handleFormDocumentSelection = (formDocumentId: string) => {
    setFormDocumentId(formDocumentId);
  };

  const onUpdateMapping = () => {
    const updatedApprovalStages = approvalStages.map(approvalStage => {
      const editableFields = approvalStage.mappedFrom?.map(mappedFromEntry => mappedFromEntry.name);
      return { ...approvalStage, editableFields };
    });
    handleMappingUpdate(updatedApprovalStages, selectedDocumentTypeId, formDocumentId);
  };

  return (
    <Dialog
      disableEscapeKeyDown
      disableBackdropClick
      open={mappingDialog.isOpen}
      maxWidth="xl"
      fullWidth
      classes={{
        paper: classes.paper,
      }}
    >
      <DialogTitle>
        <Box className={classes.genericColumnFlex}>
          <Typography variant="h3">
            {translate('approval.configuration.stage.mapping.dialog.title')}
          </Typography>
          <Typography variant="subtitle2" color="textSecondary" className={classes.subtitle}>
            <Text translation="approval.configuration.stage.mapping.dialog.subtitle1" />
          </Typography>
          <Typography variant="subtitle2" color="textSecondary" className={classes.subtitle}>
            <Text translation="approval.configuration.stage.mapping.dialog.subtitle2" />
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormControl id="document-type-selectField" className={classes.fullWidth}>
              <Autocomplete
                options={documentTypeOptions}
                getOptionLabel={(type) => `${type.text} • ${type.documentTypeAcronym}`}
                placeholder={translate('approval.configuration.stage.mapping.dialog.document.type')}
                data-cy="doc-type"
                value={documentTypeOptions.find((type) => type.id === selectedDocumentTypeId) || null}
                onChange={(_, selectedOption) =>
                  setSelectedDocumentTypeId(selectedOption?.id || '')
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl className={classes.fullWidth}>
              <Autocomplete
                options={formRevsList}
                placeholder={translate('approval.configuration.stage.mapping.dialog.document.form.rev.selection')}
                getOptionLabel={(formRev) =>
                  translate('approval.configuration.stage.mapping.dialog.document.revision.options.label', {
                    docId: formRev.document?.docId,
                    name: formRev.name,
                    revision: formRev.displayRevision,
                    status: formRev.displayStatus,
                  })
                }
                data-cy="from-rev-selection"
                value={formRevsList.find((rev) => rev.id === formRevId) || null}
                onChange={(_, selectedOption: DocumentRevision) => {
                  handleFormDocumentSelection(selectedOption?.document.id);
                }}
              />
            </FormControl>
          </Grid>
        </Grid>

        {isLoading && <Loader />}

        {
          Boolean(formRevSchema?.length && approvalStages?.length)
            && <Box className={classes.mappingArea}>
              <ArcherContainer
                strokeColor={Colors.textGray}
                strokeWidth={0.75}
                className={cx(classes.spaceBetweenFlex, classes.fullWidth)}
              >
                <Box className={classes.fromRevContainer}>
                  <Typography variant="h5" className={classes.mappingAreaTitle}>
                    {translate('approval.configuration.stage.mapping.dialog.mapping.fromRev.schema.items.label')}
                  </Typography>
                  <Chip
                    className={classes.editableFieldsChip}
                    size="small"
                    label={`${unmappedFieldsCount} ${
                      unmappedFieldsCount === 1 ? 'Field' : 'Fields'
                    } not mapped`}
                  />
                  <Box className={cx({ [classes.mappingView]: !isShowRelationVisible })}>
                    {formRevSchema
                      .filter((schemaItem) => !schemaItem.deleted)
                      .map((schemaItem) => {
                        return (
                          <ArcherElement id={`from-${schemaItem.name!}`} key={`from-${schemaItem.name!}`}>
                            <Box
                              className={cx(classes.mappingRuleSchemaItemBox, classes.pointer)}
                              style={{ backgroundColor: getFromSchemaItemBgColor(schemaItem) }}
                              onClick={(e) => handleFromSchemaItemClick(schemaItem)}
                            >
                              <Typography className={classes.schemaItemLabel} color="primary">
                                {schemaItem.label} {checkIfRequired(schemaItem)}
                              </Typography>
                              <Typography variant="subtitle2" display="block">
                                {schemaItem.name}
                              </Typography>
                              {selectedSchemaItem?.name === schemaItem.name && (
                                <FontAwesomeIcon icon={solid('check-circle')} className={classes.selectedFromSchemaItem} />
                              )}
                            </Box>
                          </ArcherElement>
                        );
                      })}
                  </Box>
                </Box>
                <Box className={classes.toStageContainer}>
                  <Typography variant="h5" className={classes.mappingAreaTitle}>
                    {translate('approval.configuration.stage.mapping.dialog.mapping.approval.stages.label')}
                  </Typography>
                  <Box className={cx({ [classes.mappingView]: !isShowRelationVisible })}>
                    {approvalStages
                      .map((approvalStage) => {
                        return (
                          <ArcherElement
                            id={`${approvalStage.stageNumber}`}
                            key={`to-${approvalStage.stageNumber}`}
                            relations={
                              isShowRelationVisible
                                ? approvalStage.mappedFrom?.map((mappedFromEntry) => ({
                                  targetId: `from-${mappedFromEntry.name}`,
                                  targetAnchor: 'right',
                                  sourceAnchor: 'left',
                                }))
                                : []
                            }
                          >
                            <Box
                              className={cx(classes.mappingRuleSchemaItemBox, classes.toSchemaItem)}
                              onClick={() => handleApprovalStageClick(approvalStage)}
                            >
                              <Box className={classes.fullWidth}>
                                <Box className={classes.simpleFlex}>
                                  <Typography className={classes.schemaItemLabel} color="primary">
                                    {approvalStage.name}
                                  </Typography>
                                  <Chip
                                    className={classes.editableFieldsChip}
                                    size="small"
                                    label={`${approvalStage.mappedFrom?.length} ${
                                        approvalStage.mappedFrom?.length === 1 ? 'Field' : 'Fields'
                                      }`}
                                  />
                                </Box>
                              </Box>
                              {(approvalStage.mappedFrom ?? []).length > 0
                                && approvalStage.mappedFrom?.map((mappedFromEntry: SourceMapping) => {
                                  const { label, name } = mappedFromEntry;
                                  return (
                                    <Chip
                                      key={`${name}_${label}`}
                                      label={label}
                                      onDelete={() => handleMappedFromDelete(approvalStage, mappedFromEntry)}
                                      color="secondary"
                                      className={classes.chip}
                                    />
                                  );
                                })}
                            </Box>
                          </ArcherElement>
                        );
                      })}
                  </Box>
                </Box>
              </ArcherContainer>
            </Box>
        }
      </DialogContent>

      <Grid container className={classes.actionBtns}>
        <Grid item xs={4}>
          {selectedDocumentTypeId && formDocumentId
            && <Box className={classes.genericFlex}>
              <FormControlLabel
                className={classes.selectControl}
                control={
                  <Checkbox
                    checked={isShowRelationVisible}
                    onChange={(event) => setIsShowRelationVisible(event.target.checked)}
                    color="primary"
                  />
                }
                label={translate('approval.configuration.stage.mapping.dialog.show.mappings')}
              />
              <StyledButton
                onClick={onClearMappings}
                variant="text"
              >
                <Text translation="approval.configuration.stage.mapping.dialog.clear.mappings" />
              </StyledButton>
            </Box>
          }
        </Grid>
        <Grid item xs={6} className={classes.btnGroup}>
          <Button
            onClick={mappingDialog.close}
            variant="outlined"
          >
            <Text translation="common.cancel" />
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={onUpdateMapping}
          >
            <Text translation="common.update" />
          </Button>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default StageFieldMappingDialog;
